import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AudioProvider } from './contexts/AudioContext';
import Home from './pages/Home';
import Channels from './pages/Channels';
import ChannelDetail from './pages/ChannelDetail';
import ShowDetail from './pages/ShowDetail';
import EpisodeDetail from './pages/EpisodeDetail';
import Episodes from './pages/Episodes';
import SessionPlayer from './pages/SessionPlayer';
import Panelists from './pages/Panelists';
import Navbar from './components/Navbar';
import FloatingPlayer from './components/FloatingPlayer';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if user was previously authenticated
    return localStorage.getItem('inews_authenticated') === 'true';
  });
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSignIn = async () => {
    setLoading(true);
    // Simulate authentication delay
    setTimeout(() => {
      setIsAuthenticated(true);
      localStorage.setItem('inews_authenticated', 'true');
      setLoading(false);
    }, 1000);
  };

  const handleSignOut = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('inews_authenticated');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Welcome to iNews</h1>
          <p className="text-gray-600 dark:text-gray-400">Preparing your AI news experience...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <div className="mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white font-bold">🎧</span>
              </div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent mb-4">
                iNews
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-2">
                Your AI News Companion
              </p>
              <p className="text-gray-500 dark:text-gray-400">
                Skip the boring news. Listen to AI panelists have real conversations.
              </p>
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center space-x-3 text-left">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 dark:text-purple-400">🎯</span>
                </div>
                <span className="text-gray-700 dark:text-gray-300">AI-powered discussions</span>
              </div>
              <div className="flex items-center space-x-3 text-left">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 dark:text-blue-400">🎧</span>
                </div>
                <span className="text-gray-700 dark:text-gray-300">Spatial audio experience</span>
              </div>
              <div className="flex items-center space-x-3 text-left">
                <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900 rounded-full flex items-center justify-center">
                  <span className="text-pink-600 dark:text-pink-400">⚡</span>
                </div>
                <span className="text-gray-700 dark:text-gray-300">Always fresh content</span>
              </div>
            </div>

            <button
              onClick={handleSignIn}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              🎧 Start Listening Now
            </button>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <AudioProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
              <Navbar onSignOut={handleSignOut} />
              <main className="container mx-auto px-4 py-8 pb-20 md:pb-8">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/channels" element={<Channels />} />
                  <Route path="/channels/:channelId" element={<ChannelDetail />} />
                  <Route path="/channels/:channelId/shows" element={<ShowDetail />} />
                  <Route path="/channels/:channelId/shows/:showId" element={<ShowDetail />} />
                  <Route path="/channels/:channelId/shows/:showId/episodes" element={<ShowDetail />} />
                  <Route path="/channels/:channelId/shows/:showId/episodes/:episodeId" element={<EpisodeDetail />} />
                  <Route path="/shows/:showId" element={<Episodes />} />
                  <Route path="/session/:sessionId" element={<SessionPlayer />} />
                  <Route path="/panelists" element={<Panelists />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
              <FloatingPlayer />
            </div>
          </Router>
        </AudioProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
